/* dynamicLines.js — THE ONE injector for the runtime BS/CFS rows.
 *
 * ★ PRINCIPLE 0 (CLAUDE.md §6). Two families of rows are not part of a tenant's chart of
 * accounts and therefore cannot come from `bs_actuals_meta`: fundraising rounds and
 * implementation-deferred revenue. They are RUNTIME objects built by the orchestrator, and
 * they have to be spliced into an already-built skeleton. Until Build A1 that splice was
 * implemented FOUR times — computeBS, BalanceSheetPage, computeCFS, CashFlowPage — and the
 * copies had already drifted: the engines emitted rounds newest-first, both pages
 * `.reverse()`d them to oldest-first, so the statement the operator READ was ordered
 * differently from the statement the model COMPUTED. Nobody noticed because
 * `fundraising_rounds` has never had a row on any tenant.
 *
 * This module is that splice, once. Same contract as `assembleISSkeleton`: it takes a
 * skeleton plus runtime objects and returns an ordered skeleton. IT COMPUTES NOT ONE NUMBER
 * — values are read later, by leaf id, from the engines' output.
 *
 * ★ BOTH INJECTORS LIVE IN ONE FILE ON PURPOSE (Mantra #7). Every injected CFS row is a
 * DERIVED MIRROR of an injected BS row — it carries `bsKey` pointing at that row's id and
 * reads its month-over-month delta. Add a family to one statement without the other and the
 * CFS references a balance-sheet row that does not exist. Co-locating the twins makes "you
 * cannot change one without seeing the other" structural instead of remembered.
 *
 * ORDER: frLines and implDeferredLines are emitted VERBATIM (Option 1, confirmed 2026-07-29).
 * That is the ordering the data layer deliberately produces — `ORDER BY close_date DESC,
 * created_at DESC` (server.js) — carried through ModelProvider (which filters but never
 * re-sorts) and the orchestrator (which pushes equity-then-cost per round), and it is the
 * rule CLAUDE.md §7 already states: "equity before issuance costs, most recent round first".
 * ★ IF THE PRESENTATION EVER CHANGES TO CHRONOLOGICAL, THE SORT GOES HERE — one line, one
 * place — and §7 changes in the same commit. Never re-order at a consumer; a consumer-side
 * `.reverse()` is exactly what produced the divergence this module exists to delete.
 *
 * PURE: no React, no DB, no engine import. The only side effect is an optional warning sink
 * (`opts.onWarn`, default `console.warn`) for a missing anchor — the same posture
 * buildISLines takes for orphan leaves.
 */

/* Anchor ids. Named so a reader can see the four splice points at once, and so a rename in
   the builders fails in one place instead of four. */
const BS_FR_ANCHOR = 'equity_header';                      // fr rows go immediately AFTER this
const BS_FR_TOTAL = 'total_equity';
const BS_IMPL_ANCHOR = 'total_current_liabilities';        // impl rows go immediately BEFORE this
const CFS_FR_ANCHOR = 'fin_header';                        // fr rows go immediately AFTER this
const CFS_FR_TOTAL = 'total_fin';
const CFS_IMPL_ANCHOR = 'total_chg_wc';                    // impl rows go immediately BEFORE this

/* ★ THE SWEEP IS THE PLUG AND IT READS LAST. `total_fin.sumOf` is REBUILT rather than
   appended to: strip 'mm_sweep', add the fundraising ids, put 'mm_sweep' back at the end.
   Financing activity is what the sweep responds to, so a fundraising inflow must sit above
   it in the section, not below.
   ★ This rule existed in exactly ONE of the four copies before A1 (computeCFS) — the two page
   copies never touched `total_fin.sumOf` at all, and nobody noticed because a page's `sumOf`
   is never read (values come from the engine by leaf id). It is the single most losable rule
   in this module, which is why it is a named function rather than an inline spread. */
function withSweepLast(sumOf, addedIds) {
  return [...sumOf.filter(x => x !== 'mm_sweep'), ...addedIds, 'mm_sweep'];
}

const nonEmpty = (a) => Array.isArray(a) && a.length > 0;

function warnMissingAnchor(onWarn, statement, family, anchorId) {
  const msg = `[dynamicLines] ${statement} skeleton has no "${anchorId}" row — the ${family} `
    + `rows were NOT injected and its total was left alone. The statement is missing those `
    + `rows; check the chart of accounts / builder output for this tenant.`;
  if (typeof onWarn === 'function') onWarn(msg);
}

/* ★ ATOMIC PER FAMILY — a deliberate narrowing of the old behavior, not a port of it.
   The four old copies used TWO INDEPENDENT `if` blocks per family: one for the row splice,
   one for the sumOf extension. If the row anchor was absent but the total was present, the
   rows were silently dropped WHILE the total went on naming their ids — a phantom reference
   that inflates nothing but makes the total's sumOf describe rows that do not exist, and
   which the new BS/CFS leaf-in-one-total check (preflightCheck P1-BS/P1-CFS) would then
   report. Here a family is injected wholly or not at all, and the skip is NAMED.
   Provably behavior-identical on live data: every anchor exists on cid=1/2/3/8 (measured),
   and the two impl families already keyed both operations off a single findIndex. */

/**
 * Splice the runtime BS rows into a built balance-sheet skeleton.
 * @param {Array} baseBsLines            ordered skeleton from buildBSLinesForCompany
 * @param {Array} frLines                [{ bsId, cfsId, label, type:'equity'|'cost' }]
 * @param {Array} implDeferredLines      [{ bsId, cfsId, label }]
 * @param {{onWarn?:(m:string)=>void}} [opts]
 * @returns {Array} a NEW array when anything was injected; otherwise `baseBsLines` ITSELF.
 */
export function injectDynamicBSLines(baseBsLines, frLines, implDeferredLines, opts = {}) {
  const onWarn = opts.onWarn === undefined ? console.warn : opts.onWarn;
  const hasFr = nonEmpty(frLines);
  const hasImpl = nonEmpty(implDeferredLines);
  // ★ FAST PATH — return the SAME REFERENCE when there is nothing to inject. This is what
  // makes A1 provably inert for every tenant without fundraising rounds or deferred
  // implementation lines (cid=2/3/8 today): the consumer gets the identical array object it
  // passed in, so no downstream memo, diff, or identity check can observe a change.
  if (!baseBsLines || (!hasFr && !hasImpl)) return baseBsLines;

  let lines = [...baseBsLines];

  if (hasFr) {
    const anchorIdx = lines.findIndex(l => l.id === BS_FR_ANCHOR);
    if (anchorIdx < 0) {
      warnMissingAnchor(onWarn, 'Balance Sheet', 'fundraising', BS_FR_ANCHOR);
    } else {
      // frLines is already ordered equity-then-cost per round, newest round first — insert as-is.
      const frBsLines = frLines.map(fr => ({ id: fr.bsId, label: fr.label, level: 1 }));
      lines.splice(anchorIdx + 1, 0, ...frBsLines);

      const teIdx = lines.findIndex(l => l.id === BS_FR_TOTAL);
      if (teIdx >= 0) {
        const frIds = frLines.map(fr => fr.bsId);
        lines[teIdx] = { ...lines[teIdx], sumOf: [...frIds, ...lines[teIdx].sumOf] };
      } else {
        warnMissingAnchor(onWarn, 'Balance Sheet', 'fundraising total', BS_FR_TOTAL);
      }
    }
  }

  if (hasImpl) {
    // Phase 3b splice-anchor fix, preserved: total_current_liabilities is an ALWAYS-PRESENT
    // synthetic, unlike deferred_rev_company_quota (absent for tenants without CQ → the old
    // findIndex returned -1 and spliced at index 0).
    const tclIdx = lines.findIndex(l => l.id === BS_IMPL_ANCHOR);
    if (tclIdx < 0) {
      warnMissingAnchor(onWarn, 'Balance Sheet', 'implementation-deferred', BS_IMPL_ANCHOR);
    } else {
      const implBsLines = implDeferredLines.map(il => ({ id: il.bsId, label: il.label, level: 2 }));
      const implBsIds = implDeferredLines.map(il => il.bsId);
      lines.splice(tclIdx, 0, ...implBsLines);
      const newTclIdx = tclIdx + implBsLines.length;   // the anchor moved down by the rows inserted above it
      lines[newTclIdx] = { ...lines[newTclIdx], sumOf: [...lines[newTclIdx].sumOf, ...implBsIds] };
    }
  }

  return lines;
}

/**
 * Splice the runtime CFS rows into a built cash-flow skeleton.
 * Each injected row is a BS-delta reader: `source:'bs'` + `bsKey` (the injected BS row's id)
 * + `sign:'l'` (liability convention — a balance increase is cash in). It therefore REQUIRES
 * that injectDynamicBSLines ran on the same frLines / implDeferredLines.
 * @returns {Array} a NEW array when anything was injected; otherwise `baseCfsLines` ITSELF.
 */
export function injectDynamicCFSLines(baseCfsLines, frLines, implDeferredLines, opts = {}) {
  const onWarn = opts.onWarn === undefined ? console.warn : opts.onWarn;
  const hasFr = nonEmpty(frLines);
  const hasImpl = nonEmpty(implDeferredLines);
  if (!baseCfsLines || (!hasFr && !hasImpl)) return baseCfsLines;   // fast path — same reference

  let lines = [...baseCfsLines];

  if (hasFr) {
    const anchorIdx = lines.findIndex(l => l.id === CFS_FR_ANCHOR);
    if (anchorIdx < 0) {
      warnMissingAnchor(onWarn, 'Cash Flow', 'fundraising', CFS_FR_ANCHOR);
    } else {
      // Top of financing, matching the BS order — same frLines, same sequence.
      const frCfsLines = frLines.map(fr => ({
        id: fr.cfsId, label: `Increase/(Decrease) ${fr.label}`, level: 1,
        source: 'bs', bsKey: fr.bsId, sign: 'l',
      }));
      lines.splice(anchorIdx + 1, 0, ...frCfsLines);

      const tfIdx = lines.findIndex(l => l.id === CFS_FR_TOTAL);
      if (tfIdx >= 0) {
        const frCfsIds = frLines.map(fr => fr.cfsId);
        lines[tfIdx] = { ...lines[tfIdx], sumOf: withSweepLast(lines[tfIdx].sumOf, frCfsIds) };
      } else {
        warnMissingAnchor(onWarn, 'Cash Flow', 'fundraising total', CFS_FR_TOTAL);
      }
    }
  }

  if (hasImpl) {
    // Phase 3b splice-anchor fix, preserved (see the BS twin above).
    const twcIdx = lines.findIndex(l => l.id === CFS_IMPL_ANCHOR);
    if (twcIdx < 0) {
      warnMissingAnchor(onWarn, 'Cash Flow', 'implementation-deferred', CFS_IMPL_ANCHOR);
    } else {
      const implCfsLines = implDeferredLines.map(il => ({
        id: il.cfsId, label: `Increase/(Decrease) ${il.label}`, level: 2,
        source: 'bs', bsKey: il.bsId, sign: 'l',
      }));
      const implCfsIds = implDeferredLines.map(il => il.cfsId);
      lines.splice(twcIdx, 0, ...implCfsLines);
      const newTwcIdx = twcIdx + implCfsLines.length;
      lines[newTwcIdx] = { ...lines[newTwcIdx], sumOf: [...lines[newTwcIdx].sumOf, ...implCfsIds] };
    }
  }

  return lines;
}
